import React, { useState, useEffect, useRef } from 'react';
import { InAppMessageDto, CreateInAppMessageRequest, UpdateInAppMessageRequest, MESSAGE_TYPES, PRIORITY_LEVELS, ACTION_TYPES } from '../../types/InAppMessage';

import HtmlEditor, { HtmlEditorHandle } from './HtmlEditor';
import HtmlPreview from './HtmlPreview';

import GeofenceMap from './GeofenceMap';


interface MessageFormProps {
  message?: InAppMessageDto;
  onSubmit: (data: CreateInAppMessageRequest | UpdateInAppMessageRequest) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

const MessageForm: React.FC<MessageFormProps> = ({ message, onSubmit, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState<CreateInAppMessageRequest>({
    title: '',
    content: '',
    expiresAt: '',
    showStartDate: '',
    showEndDate: '',
    priority: 1,
    messageType: 'info',
    targetAudience: '',
    targetRegions: '',
    userId: '',
    deviceId: '',
    actionType: '',
    actionData: '',
    imageUrl: '',
    metadata: '',
    geoFenceData: ''
  });
  const [loading, setLoading] = useState(false);

  const htmlEditorRef = useRef<HtmlEditorHandle>(null);

  const [geofenceData, setGeofenceData] = useState<any>(null);
  const [geofenceTrigger, setGeofenceTrigger] = useState<'enter' | 'exit' | 'dwell'>('enter');


  useEffect(() => {
    if (message) {
      setFormData({
        title: message.title,
        content: message.content,
        expiresAt: message.expiresAt ? message.expiresAt.split('T')[0] + 'T' + message.expiresAt.split('T')[1].split('.')[0] : '',
        showStartDate: message.showStartDate ? message.showStartDate.split('T')[0] + 'T' + message.showStartDate.split('T')[1].split('.')[0] : '',
        showEndDate: message.showEndDate ? message.showEndDate.split('T')[0] + 'T' + message.showEndDate.split('T')[1].split('.')[0] : '',
        priority: message.priority,
        messageType: message.messageType,
        targetAudience: message.targetAudience || '',
        targetRegions: message.targetRegions || '',
        userId: message.userId || '',
        deviceId: message.deviceId || '',
        actionType: message.actionType || '',
        actionData: message.actionData || '',
        imageUrl: message.imageUrl || '',
        metadata: message.metadata || '',
        geoFenceData: message.geoFenceData || ''
      });
      
      // Parse existing geofence data
      if (message.geoFenceData) {
        try {
          const geoData = JSON.parse(message.geoFenceData);
          setGeofenceData(geoData);
          if (geoData.trigger) {
            setGeofenceTrigger(geoData.trigger);
          }
        } catch (e) {
          console.error('Error parsing existing geofence data:', e);
        }
      }
    }
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get HTML content from the editor
      const htmlContent = htmlEditorRef.current?.getContent() || formData.content;
      
      const submitData = {
        ...formData,
        content: htmlContent,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : undefined,
        showStartDate: formData.showStartDate ? new Date(formData.showStartDate).toISOString() : undefined,
        showEndDate: formData.showEndDate ? new Date(formData.showEndDate).toISOString() : undefined,
        targetAudience: formData.targetAudience || undefined,
        targetRegions: formData.targetRegions || undefined,
        userId: formData.userId || undefined,
        deviceId: formData.deviceId || undefined,
        actionType: formData.actionType || undefined,
        actionData: formData.actionData || undefined,
        imageUrl: formData.imageUrl || undefined,
        metadata: formData.metadata || undefined,
        geoFenceData: formData.geoFenceData || undefined
      };
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' ? parseInt(value) : value
    }));
  };


  const handleContentChange = (html: string) => {
    setFormData(prev => ({
      ...prev,
      content: html
    }));
  };

  const handleGeofenceChange = (data: any) => {
    setGeofenceData(data);
    if (data) {
      // Update the trigger type in the geofence data
      const updatedData = {
        ...data,
        trigger: geofenceTrigger
      };
      setFormData(prev => ({
        ...prev,
        geoFenceData: JSON.stringify(updatedData)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        geoFenceData: ''
      }));
    }
  };

  const handleTriggerChange = (trigger: 'enter' | 'exit' | 'dwell') => {
    setGeofenceTrigger(trigger);
    if (geofenceData) {
      const updatedData = {
        ...geofenceData,
        trigger
      };
      setFormData(prev => ({
        ...prev,
        geoFenceData: JSON.stringify(updatedData)
      }));
    }

  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">
          {isEdit ? 'Edit Message' : 'Create New Message'}
        </h2>
      </div>
      
      <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            required
            value={formData.title}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter message title"
          />
        </div>

        {/* Content */}
        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
            Content * <span className="text-sm text-gray-500">(Rich HTML Editor)</span>
          </label>
          <HtmlEditor
            ref={htmlEditorRef}
            value={formData.content}
            onChange={handleContentChange}
            placeholder="Enter your message content. You can use formatting, links, images, and more!"
            height="300px"
          />
          <p className="mt-1 text-sm text-gray-500">
            Use the toolbar above to format your message with bold text, links, images, and more. 
            The mobile app will display this as rich HTML content.
          </p>
        </div>

        {/* Content Preview */}
        {formData.content && (
          <div>
            <HtmlPreview 
              htmlContent={formData.content} 
              title={formData.title}
            />
          </div>
        )}

        {/* Priority and Message Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {PRIORITY_LEVELS.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="messageType" className="block text-sm font-medium text-gray-700">
              Message Type
            </label>
            <select
              id="messageType"
              name="messageType"
              value={formData.messageType}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {MESSAGE_TYPES.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Expires At */}
        <div>
          <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">
            Expires At
          </label>
          <input
            type="datetime-local"
            id="expiresAt"
            name="expiresAt"
            value={formData.expiresAt}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          <p className="mt-1 text-sm text-gray-500">When the message should be permanently removed</p>
        </div>

        {/* Scheduling Section */}
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Message Scheduling</h3>
          <p className="text-sm text-gray-600 mb-4">Control when the message is shown to users. Messages are downloaded but only displayed during the scheduled window.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="showStartDate" className="block text-sm font-medium text-gray-700">
                Show Start Date
              </label>
              <input
                type="datetime-local"
                id="showStartDate"
                name="showStartDate"
                value={formData.showStartDate}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-sm text-gray-500">Leave empty to show immediately</p>
            </div>

            <div>
              <label htmlFor="showEndDate" className="block text-sm font-medium text-gray-700">
                Show End Date
              </label>
              <input
                type="datetime-local"
                id="showEndDate"
                name="showEndDate"
                value={formData.showEndDate}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-sm text-gray-500">Leave empty to show indefinitely</p>
            </div>
          </div>
        </div>

        {/* Action Type and Data */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="actionType" className="block text-sm font-medium text-gray-700">
              Action Type
            </label>
            <select
              id="actionType"
              name="actionType"
              value={formData.actionType}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">None</option>
              {ACTION_TYPES.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="actionData" className="block text-sm font-medium text-gray-700">
              Action Data (JSON)
            </label>
            <textarea
              id="actionData"
              name="actionData"
              rows={3}
              value={formData.actionData}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder='{"url": "https://example.com"}'
            />
          </div>
        </div>

        {/* Target Audience and Regions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700">
              Target Audience (JSON Array)
            </label>
            <textarea
              id="targetAudience"
              name="targetAudience"
              rows={3}
              value={formData.targetAudience}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder='["all_users", "premium_users"]'
            />
          </div>

          <div>
            <label htmlFor="targetRegions" className="block text-sm font-medium text-gray-700">
              Target Regions (JSON Array)
            </label>
            <textarea
              id="targetRegions"
              name="targetRegions"
              rows={3}
              value={formData.targetRegions}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder='[1, 2, 3]'
            />
          </div>
        </div>

        {/* User/Device Targeting */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="userId" className="block text-sm font-medium text-gray-700">
              Target User ID
            </label>
            <input
              type="text"
              id="userId"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional: specific user ID"
            />
            <p className="mt-1 text-sm text-gray-500">Leave empty for all users</p>
          </div>

          <div>
            <label htmlFor="deviceId" className="block text-sm font-medium text-gray-700">
              Target Device ID
            </label>
            <input
              type="text"
              id="deviceId"
              name="deviceId"
              value={formData.deviceId}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional: specific device ID"
            />
            <p className="mt-1 text-sm text-gray-500">Leave empty for all devices</p>
          </div>
        </div>

        {/* Image URL */}
        <div>
          <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700">
            Image URL
          </label>
          <input
            type="url"
            id="imageUrl"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="https://example.com/image.jpg"
          />
        </div>

        {/* Metadata */}
        <div>
          <label htmlFor="metadata" className="block text-sm font-medium text-gray-700">
            Metadata (JSON)
          </label>
          <textarea
            id="metadata"
            name="metadata"
            rows={3}
            value={formData.metadata}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder='{"category": "announcement", "author": "admin"}'
          />
        </div>

        {/* Geofence Configuration */}
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Geofence Configuration</h3>
          <p className="text-sm text-gray-600 mb-4">Configure location-based targeting for this message. Users will receive the message when they interact with the defined geographic area.</p>
          
          {/* Trigger Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trigger Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="geofenceTrigger"
                  value="enter"
                  checked={geofenceTrigger === 'enter'}
                  onChange={() => handleTriggerChange('enter')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">On Enter</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="geofenceTrigger"
                  value="exit"
                  checked={geofenceTrigger === 'exit'}
                  onChange={() => handleTriggerChange('exit')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">On Exit</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="geofenceTrigger"
                  value="dwell"
                  checked={geofenceTrigger === 'dwell'}
                  onChange={() => handleTriggerChange('dwell')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">While Inside</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              • <strong>On Enter:</strong> Trigger when user enters the area<br/>
              • <strong>On Exit:</strong> Trigger when user leaves the area<br/>
              • <strong>While Inside:</strong> Trigger while user is within the area
            </p>
          </div>
          
          {/* Map Component */}
          <GeofenceMap
            onGeofenceChange={handleGeofenceChange}
            initialGeofence={formData.geoFenceData}
            height="400px"
          />
          
          {/* Current Geofence Data Display */}
          {geofenceData && (
            <div className="mt-4 p-3 bg-white rounded border">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Current Geofence</h4>
              <div className="text-xs text-gray-600">
                <p><strong>Shape:</strong> {geofenceData.shape}</p>
                <p><strong>Trigger:</strong> {geofenceTrigger}</p>
                <p><strong>Active:</strong> {geofenceData.isActive ? 'Yes' : 'No'}</p>
                {geofenceData.polygonPoints && (
                  <p><strong>Points:</strong> {geofenceData.polygonPoints.length} coordinates</p>
                )}
                {geofenceData.id && (
                  <p><strong>ID:</strong> {geofenceData.id}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Message' : 'Create Message')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageForm;